import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { AvReasonDialogComponent } from '../../../../Shared/Components/av-reason-dialog/av-reason-dialog.component';
import { AvEmptyStateComponent } from '../../../../Shared/Components/av-empty-state/av-empty-state.component';
import { AvPaginatorComponent } from '../../../../Shared/Components/av-paginator/av-paginator.component';
import { AvStatusChipComponent } from '../../../../Shared/Components/av-status-chip/av-status-chip.component';
import { AvOrganizationPickerComponent } from '../../../../Shared/Components/av-organization-picker/av-organization-picker.component';
import { DeviceEstateFacade } from '../../../../../Facades/Devices/device-estate.facade';
import { DATE_TIME_FORMAT, VALUE_UNRESOLVED } from '../../../../../Consts/backoffice.const';
import {
  DEVICE_RETIRE_CONFIRM,
  DEVICE_RETIRE_CONSEQUENCE,
  DEVICE_RETIRE_TITLE,
} from '../../../../../Consts/devices.const';
import { DEVICE_STATE_LABEL, DEVICE_STATE_OPTIONS } from '../../../../../Enums/device-state.enum';
import { Device } from '../../../../../Models/backoffice/device.model';
import { DeviceFilters } from '../../../../Organizations/Interfaces/device-filters.interface';
import { ReasonDialogResult } from '../../../../Shared/Interfaces/reason-dialog.interface';

@Component({
  selector: 'app-device-estate',
  standalone: true,
  imports: [
    DatePipe,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    AvEmptyStateComponent,
    AvPaginatorComponent,
    AvStatusChipComponent,
    AvOrganizationPickerComponent,
  ],
  templateUrl: './device-estate.component.html',
  styleUrl: './device-estate.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-block' },
})
export class DeviceEstateComponent {
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _dialog = inject(MatDialog);

  readonly facade = inject(DeviceEstateFacade);

  readonly dateTimeFormat = DATE_TIME_FORMAT;
  readonly unresolved = VALUE_UNRESOLVED;
  readonly stateOptions = DEVICE_STATE_OPTIONS;
  readonly stateLabelKeys = DEVICE_STATE_LABEL;

  private readonly _query = toSignal(this._route.queryParamMap);

  readonly isEmpty = computed<boolean>(
    () => this.facade.state() === 'ready' && this.facade.devices().length === 0,
  );

  constructor() {
    this._registerOrganizationQueryParam();
  }

  countFor(state: string): number {
    return this.facade.counts()[state] ?? 0;
  }

  selectOrganization(organizationId: string): void {
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { organization: organizationId || null },
      queryParamsHandling: 'merge',
    });
  }

  changeFilter(field: keyof DeviceFilters, value: string): void {
    this.facade.applyFilters({ ...this.facade.filters(), [field]: value });
  }

  clearFilters(): void {
    this.facade.clearFilters();
  }

  showReapable(): void {
    this.facade.showReapable();
  }

  selectDevice(deviceId: string): void {
    this.facade.selectDevice(deviceId);
  }

  goToPage(page: number): void {
    this.facade.goToPage(page);
  }

  changePageSize(pageSize: number): void {
    this.facade.changePageSize(pageSize);
  }

  reload(): void {
    this.facade.reload();
  }

  openRetire(device: Device): void {
    const reference = this._dialog.open(AvReasonDialogComponent, {
      data: {
        title: DEVICE_RETIRE_TITLE,
        consequence: DEVICE_RETIRE_CONSEQUENCE,
        organizationName: this.facade.organizationName(),
        subjectLabel: device.displayName,
        confirmLabel: DEVICE_RETIRE_CONFIRM,
        isDangerous: true,
      },
    });

    reference
      .afterClosed()
      .subscribe((result?: ReasonDialogResult) => this._retire(device.id, result));
  }

  private _registerOrganizationQueryParam(): void {
    effect(() => {
      this.facade.selectOrganization(this._query()?.get('organization') ?? '');
    });
  }

  private _retire(deviceId: string, result?: ReasonDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.retire(deviceId, result.reason);
  }
}
